import { UnderwritingRequestDetail, UnderwritingDashboardModel, QuoteSnapshotDocument, RequestTypeRow, AgentPerformanceRow, TeamChannel, UWDashboardStatsResponse, UWQueueDashboardStats, UWQueueAgentRow, BaseApplicationDetails, PrepareApplicationResponse } from "@/models/underwriting";
import { AncillaryProductLenderStanceType, DealerType, FinanceCompany, FinanceType } from "@cf247/pricing/dist/enums";
import { DrivingLicence } from "@cf247/pricing/dist/enums/customer/driving-licence";
import {
  mockVehicle,
  mockDealer,
  mockCustomer,
  mockCloseProposal,
  mockAlpheraProposal,
  mockMotoNovoProposal,
  mockZopaProposal,
} from "@cf247/pricing/dist/mocks";

export function mockQueueClaimHistory(): import("@/models/underwriting").UnderwritingQueueClaimHistory[] {
  const now = Date.now();
  const timestamp = (minutesAgo: number) => new Date(now - minutesAgo * 60_000).toISOString();
  const periodStart = new Date(now - 30 * 24 * 60 * 60_000).toISOString();
  const periodEnd = new Date(now).toISOString();
  return [
    { underwritingQueueClaimHistoryId: 1, applicationId: 11023866, agentId: 123, claimedBy: "Daniel Reyes", claimedOn: timestamp(90), releasedOn: timestamp(60), releaseReason: "TakenOver", periodStart, periodEnd },
    { underwritingQueueClaimHistoryId: 2, applicationId: 11023866, agentId: 456, claimedBy: "Ryan Patel", claimedOn: timestamp(60), releasedOn: timestamp(10), releaseReason: "Actioned", periodStart, periodEnd },
    { underwritingQueueClaimHistoryId: 3, applicationId: 18226463, agentId: 789, claimedBy: "Hannah Brooks", claimedOn: timestamp(300), releasedOn: timestamp(250), releaseReason: "Abandoned", periodStart, periodEnd },
    { underwritingQueueClaimHistoryId: 4, applicationId: 18226463, agentId: 123, claimedBy: "Daniel Reyes", claimedOn: timestamp(240), releasedOn: timestamp(200), releaseReason: "Unclaimed", periodStart, periodEnd },
    { underwritingQueueClaimHistoryId: 5, applicationId: 18226463, agentId: 456, claimedBy: "Ryan Patel", claimedOn: timestamp(120), releasedOn: null, releaseReason: null, periodStart, periodEnd },
  ];
}

export const mockUnderwritingDashboard: UnderwritingDashboardModel = {
  stats: {
    totalRequestsInPeriod: 18,
    pendingCount: 3,
    resubmissionRatePct: 22,
    avgTurnaroundMinutes: 14,
    approvedCount: 11,
    declinedCount: 4,
  },
  recentActivity: [
    {
      quoteUnderwritingRequestId: 1001,
      applicationId: 11023866,
      resubmissionCount: null,
      customerName: "Laura Simmons",
      affiliate: "MoneySuperMarket",
      requestType: "Lender Override",
      channel: "Consumer Direct",
      createdOn: new Date(Date.now() - 10 * 60 * 1000).toISOString(),
      agentName: "Daniel Reyes",
      claimedBy: null,
      status: "Pending",
      latestDecision: null,
      requestedLender: 70, // Close Brothers
      bestLender: 73,      // Zopa
      overview: {
        submittedBy: "Laura Simmons",
        panelLenderName: "Zopa",
        requestedLenderName: "Close Brothers",
        subject: "Customer Preference",
        reason: "Customer specifically requested Close Brothers due to a prior positive experience with them.",
      },
      addOns: [
        { name: "Warranty", price: "£499", status: "In Finance" },
      ],
    },
    {
      quoteUnderwritingRequestId: 1002,
      applicationId: 18226463,
      resubmissionCount: null,
      customerName: "Olivia Grant",
      affiliate: "Motorpoint Derby",
      requestType: "Lender Override",
      channel: "Dealer Direct",
      createdOn: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
      agentName: "Hannah Brooks",
      claimedBy: "Ryan Patel",
      status: "InReview",
      latestDecision: null,
      requestedLender: 50, // Alphera
      bestLender: 73,      // Zopa
      overview: {
        submittedBy: "Ryan Patel",
        panelLenderName: "VWFS",
        requestedLenderName: "Northridge",
        subject: "Customer Preference",
        reason: "Customer specifically requested Northridge. She has an existing relationship with them and wants to keep her financial relationships consolidated.",
      },
      addOns: [
        { name: "Warranty", price: "£499", status: "In Finance" },
        { name: "Paint Protection", price: "£299", status: "Not Included" },
      ],
    },
    {
      quoteUnderwritingRequestId: 1003,
      applicationId: 18528623,
      resubmissionCount: 2,
      customerName: "Emma Clarke",
      affiliate: "Confused.com",
      requestType: "Lender Override",
      channel: "Consumer Direct",
      createdOn: new Date(Date.now() - 60 * 60 * 1000).toISOString(),
      agentName: "Marcus Webb",
      claimedBy: "Sarah Mitchell",
      status: "Pending",
      latestDecision: null,
      requestedLender: 67, // MotoNovo
      bestLender: 73,      // Zopa
      overview: {
        submittedBy: "Emma Clarke",
        panelLenderName: "Zopa",
        requestedLenderName: "MotoNovo",
        subject: "Rate Match",
        reason: "Customer has been offered a better rate by MotoNovo directly and requests we match it through the panel.",
      },
      addOns: [],
    },
    {
      quoteUnderwritingRequestId: 1004,
      applicationId: 18528624,
      resubmissionCount: null,
      customerName: "Sophie Turner",
      affiliate: "Compare the Market",
      requestType: "Lender Override",
      channel: "Consumer Direct",
      createdOn: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
      agentName: "Priya Nair",
      claimedBy: "Tom Clarke",
      status: "Pending",
      latestDecision: null,
      requestedLender: 70, // Close Brothers
      bestLender: 50,      // Alphera
      overview: {
        submittedBy: "Sophie Turner",
        panelLenderName: "Alphera",
        requestedLenderName: "Close Brothers",
        subject: "Existing Relationship",
        reason: "Customer already has a finance product with Close Brothers and prefers to consolidate.",
      },
      addOns: [],
    },
    {
      quoteUnderwritingRequestId: 1005,
      applicationId: 18528625,
      resubmissionCount: 3,
      customerName: "Rachel Adams",
      affiliate: "Direct",
      requestType: "Lender Override",
      channel: "Consumer Direct",
      createdOn: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
      agentName: "Liam Foster",
      claimedBy: null,
      status: "Pending",
      latestDecision: null,
      requestedLender: 67, // MotoNovo
      bestLender: 70,      // Close Brothers
      overview: {
        submittedBy: "Rachel Adams",
        panelLenderName: "Close Brothers",
        requestedLenderName: "MotoNovo",
        subject: "Resubmission — Revised Terms",
        reason: "Third resubmission following revised deposit and improved credit profile. Customer is committed to MotoNovo.",
      },
      addOns: [],
    },
  ],
  requestsByType: {
    rows: [
      {
        requestType: "Lender Override",
        approved: 11,
        declined: 2,
        alternative: 0,
        pending: 1,
        total: 14,
      },
      {
        requestType: "Standard",
        approved: 0,
        declined: 2,
        alternative: 0,
        pending: 2,
        total: 4,
      },
    ],
  },
  teamPerformance: {
    agents: [
      {
        agentName: "Tom Clarke",
        initials: "TC",
        avatarColor: "bg-slate-700",
        channel: "Consumer Direct" as const,
        agentType: "Standard" as const,
        approved: 11,
        declined: 5,
        alternative: 1,
        totalHandled: 18,
      },
      {
        agentName: "Sarah Mitchell",
        initials: "SM",
        avatarColor: "bg-purple-600",
        channel: "Consumer Direct" as const,
        agentType: "LO" as const,
        approved: 8,
        declined: 4,
        alternative: 1,
        totalHandled: 14,
      },
      {
        agentName: "Ryan Patel",
        initials: "RP",
        avatarColor: "bg-rose-600",
        channel: "Dealer Direct" as const,
        agentType: "Standard" as const,
        approved: 7,
        declined: 3,
        alternative: 0,
        totalHandled: 11,
      },
      {
        agentName: "James Holt",
        initials: "JH",
        avatarColor: "bg-amber-600",
        channel: "Dealer Direct" as const,
        agentType: "Standard" as const,
        approved: 5,
        declined: 2,
        alternative: 1,
        totalHandled: 9,
      },
    ],
  },
};

const mockDetailBase = (
  applicationId: number,
  customerName: string,
  affiliate: string,
  channel: "Consumer Direct" | "Dealer Direct",
  lockedBy: string | null,
  resubCount: number | null,
  minutesAgo: number
): UnderwritingRequestDetail => ({
  applicationId,
  quoteSnapshotId: null, // populated by real API
  customerName,
  type: "Lender Override",
  channel,
  status: "Pending",
  affiliate,
  submittedAt: new Date(Date.now() - minutesAgo * 60 * 1000).toISOString(),
  resubCount,
  lockedBy,
  applicationNotes: null,
  requestOverview: null,
  vehicleDetails: {
    makeModel: "2022 Volkswagen Golf GTI",
    registration: "YK22 LFP",
    colour: "Deep Black",
    mileage: "19,800 miles",
    cashPrice: "£22,500",
  },
  financeFigures: {
    cashPrice: "£22,500",
    deposit: "£2,000",
    partExchange: null,
    settlement: null,
    annualMileage: "12,000 miles",
    existingFinanceCo: null,
    loanAmount: "£20,500",
    term: "48 months",
  },
  addOns: [{ name: "Warranty", price: "£999", status: "In Finance" }],
});

export const mockApplicationDetails: Record<string, UnderwritingRequestDetail> = {
  "11023866": mockDetailBase(18198, "Laura Simmons", "MoneySuperMarket", "Consumer Direct", null, null, 8),
  "18226463": mockDetailBase(40, "Olivia Grant", "Motorpoint Derby", "Dealer Direct", "Ryan Patel", null, 20),
  "18528623": mockDetailBase(17291, "Emma Clarke", "Confused.com", "Consumer Direct", null, 2, 23),
  "18528624": mockDetailBase(16801, "Sophie Turner", "Compare the Market", "Consumer Direct", null, null, 26),
  "18528625": mockDetailBase(16401, "Rachel Adams", "Direct", "Consumer Direct", null, 3, 27),
};

/** Offline fallback for GET application/base-application-details/{applicationId}. */
export const mockBaseApplicationDetails: BaseApplicationDetails = {
  applicationId: 19063268,
  name: "Claire Hirons",
  firstName: "Claire",
  lastName: "Hirons",
  customerId: 18629151,
  dateOfBirth: "1988-02-19T00:00:00Z",
  applicationDate: "2026-08-10T14:52:00Z",
  amountToFinance: 12500,
  savedCarsCount: 0,
  applicationStatusId: 53,
  applicationStatus: "OnHold",
  applicationStatusName: "On Hold",
  preferredLender: null,
  bankDetails: {
    bankName: "Royal Bank of Scotland",
    monthsAtBank: 204,
    nameOnBankAccount: "Claire P L Hirons",
    accountNumber: "10202784",
    sortCode: "162120",
    netMonthlyIncome: 2200,
  },
  maritalStatus: { maritalStatusId: 1, maritalStatusName: "Married" },
  allocationData: {
    allocatedAgentName: "Shah Arifin",
    allocatedAgentId: 14330,
    allocatedDepartmentId: "A3",
    allocatedDepartmentName: "Prime 3",
  },
  hasThirdPartyAuthorization: false,
  hasVulnerableCustomerReported: false,
};

/** Built entirely from the pricing library's own mock factories — passes real calculate() */
export const mockQuoteSnapshot: QuoteSnapshotDocument = (() => {
  // Keep the vehicle ~2 years old relative to "now" so it stays within lenders'
  // PCP vehicle-age limits (otherwise the best lender's PCP quote never prices
  // and the "Panel #1" tag can't match any row).
  const vehicle = mockVehicle(22995, new Date().getFullYear() - 2, 18000);
  // Override VRM/colour/make/model so the UI shows meaningful data
  Object.assign(vehicle, {
    vrm: "AB21 CDE",
    make: "Volkswagen",
    model: "Golf",
    trim: "R-Line",
    derivative: "2.0 TDI 150ps",
    colour: "Indigo Blue",
  });

  const proposals = [
    mockCloseProposal(9.9),
    mockAlpheraProposal(8.5),
    mockMotoNovoProposal(11.4),
    mockZopaProposal(7.9),
  ];

  const customer = mockCustomer(
    new Date("1988-05-14"),
    2800,                          // net monthly income
    DrivingLicence.FullUKPhotocard
  );

  const dealer = mockDealer(DealerType.NonFranchisedDealer, {
    amount: 299,
    includedInFinance: true,
  });

  return {
    id: "mock-snapshot-001",
    appId: 18198,
    type: "quoteSnapshot",
    version: 1,
    createdAt: new Date().toISOString(),
    snapshot: {
      vehicles: [vehicle],
      proposals,
      customer,
      dealer,
      maxTerm: 60,
      deposit: 2000,
      pxValue: 2500,
      pxSettlement: 0,
      annualMileage: 10000,
      includedExtras: [
        {
          productId: 101,
          stance: AncillaryProductLenderStanceType.Finance,
          vehicleExtraTypeId: 3,
          vehicleExtraTypeName: "Warranty",
          soldByAgentId: 12716,
        },
      ],
      source: {
        isPrime: true,
        isSearch: false,
        affiliateId: null,
        affiliateType: null,
        poweredByLenderId: null,
      },
      bestLender: { lenderId: FinanceCompany.Zopa, financeType: FinanceType.PCP },
      requestedLender: { lenderId: FinanceCompany.CloseBrothers, financeType: FinanceType.HP },
    },
  };
})();

/** Offline fallback for GET quote-underwriting/queue/prepare-application/{applicationId}. */
export const mockPrepareApplication: PrepareApplicationResponse = {
  customer: {
    ...mockBaseApplicationDetails,
    addresses: [
      {
        postcode: "HR1 1TA",
        buildingName: "",
        subBuilding: "",
        buildingNumber: "27",
        streetName: "Brockington Drive",
        town: "Hereford",
        county: "",
        addressStatus: {
          addressStatusId: 2,
          addressStatusName: "Private Tenant",
          yearsAtAddress: 4,
          monthsAtAddress: 7,
        },
        isPrimaryAddress: true,
      },
    ],
    employmentHistory: [
      {
        occupationStatus: { occupationStatusId: 1, occupationStatusName: "Employed Full Time" },
        yearsAtEmployment: 3,
        monthsAtEmployment: 9,
        occupation: "Support worker",
        employerName: "RNC",
        isCurrentEmployment: true,
      },
    ],
    drivingLicenceType: { drivingLicenceTypeId: 2, drivingLicenceTypeName: "Full UK Driving Licence" },
    affiliateUserId: null,
    preferredLender: null,
  },
  proposals: mockQuoteSnapshot.snapshot.proposals,
  maxTerm: mockQuoteSnapshot.snapshot.maxTerm,
  deposit: mockQuoteSnapshot.snapshot.deposit,
  pxValue: mockQuoteSnapshot.snapshot.pxValue,
  pxSettlement: mockQuoteSnapshot.snapshot.pxSettlement,
  annualMileage: mockQuoteSnapshot.snapshot.annualMileage,
  quote: null,
  customerAccount: {
    applicationId: null,
    customerUserId: 2628788,
    customerId: 10661092,
    titleId: 1,
    firstName: "Alexander",
    middleNames: null,
    surname: "Hunter",
    dateOfBirth: "1982-08-08T00:00:00+01:00",
    maritalStatusId: 3,
    drvLicenceTypeId: 2,
    emailAddress: null,
    daytimePhone: null,
    eveningPhone: null,
    mobilePhone: null,
    bankAccountName: null,
    bankAccountNumber: null,
    bankSortCode: null,
    bankName: null,
    bankMonthsAt: 0,
    netMonthlyIncome: 1300,
    mailingListFlag: false,
    entryDate: null,
    town: null,
    affiliateUserId: null,
    creditCheckScore: null,
    dealerId: 33967,
    affiliateName: "Test - ignore",
    affiliatePhoneNumber1: null,
    affiliatePhoneNumber2: null,
    affiliateUserType: null,
    title: "Mr",
    maritalStatus: "Single",
  },
};

// ─── Period-keyed mock data for charts ───────────────────────────────────────

export const mockRequestsByTypePeriod: Record<"Day" | "Week" | "Month", RequestTypeRow[]> = {
  Day: [
    { requestType: "Lender Override", approved: 2, declined: 1, alternative: 0, pending: 1, total: 3 },
    { requestType: "Standard",        approved: 0, declined: 1, alternative: 0, pending: 0, total: 1 },
  ],
  Week: [
    { requestType: "Lender Override", approved: 11, declined: 2, alternative: 0, pending: 1, total: 14 },
    { requestType: "Standard",        approved: 0,  declined: 2, alternative: 0, pending: 2, total: 4  },
  ],
  Month: [
    { requestType: "Lender Override", approved: 38, declined: 9,  alternative: 2, pending: 3, total: 52 },
    { requestType: "Standard",        approved: 4,  declined: 7,  alternative: 1, pending: 1, total: 13 },
  ],
};

function scaleAgent(
  agent: Omit<AgentPerformanceRow, "initials" | "avatarColor">,
  factor: number
): Omit<AgentPerformanceRow, "initials" | "avatarColor"> {
  return {
    ...agent,
    approved:     Math.round(agent.approved * factor),
    declined:     Math.round(agent.declined * factor),
    alternative:  Math.round(agent.alternative * factor),
    totalHandled: Math.round(agent.totalHandled * factor),
  };
}

const baseAgents: Array<Omit<AgentPerformanceRow, "initials" | "avatarColor">> = [
  { agentName: "Tom Clarke",    channel: "Consumer Direct" as TeamChannel, agentType: "Standard", approved: 11, declined: 5, alternative: 1, totalHandled: 18 },
  { agentName: "Sarah Mitchell",channel: "Consumer Direct" as TeamChannel, agentType: "LO",       approved: 8,  declined: 4, alternative: 1, totalHandled: 14 },
  { agentName: "Ryan Patel",    channel: "Dealer Direct"   as TeamChannel, agentType: "Standard", approved: 7,  declined: 3, alternative: 0, totalHandled: 11 },
  { agentName: "James Holt",    channel: "Dealer Direct"   as TeamChannel, agentType: "Standard", approved: 5,  declined: 2, alternative: 1, totalHandled: 9  },
];

export const mockTeamPerformancePeriod: Record<"Day" | "Week" | "Month", Array<Omit<AgentPerformanceRow, "initials" | "avatarColor">>> = {
  Day:   baseAgents.map((a) => scaleAgent(a, 0.2)),
  Week:  baseAgents,
  Month: baseAgents.map((a) => scaleAgent(a, 4.0)),
};

// ─── Period-keyed mock dashboard stats ───────────────────────────────────────

export const mockDashboardStatsPeriod: Record<"Day" | "Week" | "Month", UWDashboardStatsResponse> = {
  Day: {
    totalRequestsInPeriod: 4,
    pendingCount: 1,
    resubmissionRatePct: 10,
    avgTurnaroundMinutes: 12,
    approvedCount: 2,
    declinedCount: 2,
    alternativeCount: 0,
    moreInfoCount: 0,
    decisionsCount: 3,
    outstandingPendingCount: 5,
    completionRatePct: 75,
    requestsTrendPct: 5.0,
    pendingTrendPct: -8.0,
    decisionsTrendPct: 6.5,
    turnaroundTrendPct: -4.2,
    completionRateTrendPct: 3.1,
    resubmissionRateTrendPct: -6.0,
  },
  Week: {
    totalRequestsInPeriod: 18,
    pendingCount: 3,
    resubmissionRatePct: 22,
    avgTurnaroundMinutes: 14,
    approvedCount: 11,
    declinedCount: 4,
    alternativeCount: 0,
    moreInfoCount: 1,
    decisionsCount: 15,
    outstandingPendingCount: 5,
    completionRatePct: 83,
    requestsTrendPct: 8.2,
    pendingTrendPct: -12.4,
    decisionsTrendPct: 11.2,
    turnaroundTrendPct: -9.3,
    completionRateTrendPct: 4.2,
    resubmissionRateTrendPct: -12.9,
  },
  Month: {
    totalRequestsInPeriod: 65,
    pendingCount: 4,
    resubmissionRatePct: 19,
    avgTurnaroundMinutes: 15,
    approvedCount: 42,
    declinedCount: 16,
    alternativeCount: 3,
    moreInfoCount: 2,
    decisionsCount: 61,
    outstandingPendingCount: 5,
    completionRatePct: 94,
    requestsTrendPct: 14.6,
    pendingTrendPct: -5.5,
    decisionsTrendPct: 13.8,
    turnaroundTrendPct: -7.1,
    completionRateTrendPct: 6.4,
    resubmissionRateTrendPct: -3.8,
  },
};

// ─── Underwriting Queue mocks ────────────────────────────────────────────────

export const mockQueueDashboardStatsPeriod: Record<"Day" | "Week" | "Month", UWQueueDashboardStats> = {
  Day: {
    avgTurnaroundMinutes: 96,
    allocatedCount: 9,
    deadedCount: 3,
    actionedCount: 12,
    outsideQueueCount: 2,
    allocatedPct: 75,
    deadedPct: 25,
    turnaroundTrendPct: -4.2,
    actionedTrendPct: 6.5,
    allocatedTrendPct: 8.1,
    deadedTrendPct: -2.4,
  },
  Week: {
    avgTurnaroundMinutes: 142,
    allocatedCount: 41,
    deadedCount: 17,
    actionedCount: 58,
    outsideQueueCount: 6,
    allocatedPct: 70.7,
    deadedPct: 29.3,
    turnaroundTrendPct: -9.3,
    actionedTrendPct: 11.2,
    allocatedTrendPct: 12.9,
    deadedTrendPct: 5.6,
  },
  Month: {
    avgTurnaroundMinutes: 168,
    allocatedCount: 164,
    deadedCount: 72,
    actionedCount: 236,
    outsideQueueCount: 23,
    allocatedPct: 69.5,
    deadedPct: 30.5,
    turnaroundTrendPct: -7.1,
    actionedTrendPct: 13.8,
    allocatedTrendPct: 15.2,
    deadedTrendPct: 9.4,
  },
};

const baseQueueAgents: UWQueueAgentRow[] = [
  { agentId: 4101, agentName: "Tom Clarke",     opened: 22, allocated: 14, deaded: 5, totalActioned: 19, unactioned: 3, avgQueueMinutes: 128, avgHandlingMinutes: 11 },
  { agentId: 4102, agentName: "Sarah Mitchell", opened: 19, allocated: 11, deaded: 4, totalActioned: 15, unactioned: 4, avgQueueMinutes: 154, avgHandlingMinutes: 17 },
  { agentId: 4103, agentName: "Ryan Patel",     opened: 17, allocated: 8,  deaded: 4, totalActioned: 12, unactioned: 5, avgQueueMinutes: 191, avgHandlingMinutes: 24 },
  { agentId: 4104, agentName: "James Holt",     opened: 11, allocated: 8,  deaded: 4, totalActioned: 12, unactioned: 0, avgQueueMinutes: 102, avgHandlingMinutes: 9  },
];

const scaleQueueAgent = (a: UWQueueAgentRow, factor: number): UWQueueAgentRow => ({
  ...a,
  opened: Math.round(a.opened * factor),
  allocated: Math.round(a.allocated * factor),
  deaded: Math.round(a.deaded * factor),
  totalActioned: Math.round(a.totalActioned * factor),
  unactioned: Math.round(a.unactioned * factor),
});

export const mockQueueTeamPerformancePeriod: Record<"Day" | "Week" | "Month", UWQueueAgentRow[]> = {
  Day: baseQueueAgents.map((a) => scaleQueueAgent(a, 0.25)),
  Week: baseQueueAgents,
  Month: baseQueueAgents.map((a) => scaleQueueAgent(a, 4.0)),
};

export const mockQueueApplications = mockUnderwritingDashboard.recentActivity;

